import { useEffect } from 'react';
import { LOCALES, LOCALE_META, ROUTE_SEGMENTS, type Locale, DEFAULT_LOCALE } from '@/hooks/useLanguage';
import { useLanguage } from '@/hooks/useLanguage';
import { LIVE_STOREFRONTS, getStorefrontConfig, useStorefront } from '@/hooks/useStorefront';
import { useSeoText } from '@/hooks/useSeoText';

const SITE_NAME = 'BigFollow';
/**
 * Legacy constant used by the exported JSON-LD helpers (organizationSchema,
 * websiteSchema, productSchema, blogPostSchema, breadcrumbSchema). Kept as the
 * CH domain in Phase A — resolves to the same value getActiveSiteUrl() returns
 * on the currently-live CH storefront, so no output changes for CH.
 */
const SITE_URL = 'https://bigfollow.ch';
const DEFAULT_DESCRIPTION = 'Follower kaufen mit TWINT – Instagram & TikTok Follower, Likes, Views günstig kaufen. Beste Preise der Schweiz. Sofortige Lieferung.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

interface HreflangEntry {
  locale: Locale;
  /** The full URL path for this locale (without domain), e.g. /en/product/buy-instagram-followers */
  path: string;
}

interface SEOHeadProps {
  title: string;
  description?: string;
  canonical?: string;
  ogType?: 'website' | 'article' | 'product';
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  /** Provide alternate paths per locale for hreflang tags */
  hreflangPaths?: HreflangEntry[];
  /**
   * P2.7 §4a — Optional override scope. When set, title/description are
   * looked up in `seo_overrides` under (scope, scope_key) instead of the
   * default ('route', pathname). Used by ProductPage/CategoryPage so an
   * override is keyed on the product/category UUID and survives slug
   * changes + covers all locales. Omit for pages that don't need it —
   * behaviour is byte-identical to before.
   *
   * CH-Fastpath: `useSeoText` returns `base` immediately for CH, so this
   * prop has zero runtime effect on the CH storefront.
   */
  overrideScope?: { scope: 'route' | 'product' | 'category'; scope_key: string };
}

/** Build hreflang entries for a simple page where the path just gets a locale prefix + segment translation */
export function buildHreflangPaths(dePath: string): HreflangEntry[] {
  const entries: HreflangEntry[] = [{ locale: 'de', path: dePath }];
  for (const loc of (['en', 'it', 'fr'] as Locale[])) {
    let translated = dePath;
    for (const [deSegment, translations] of Object.entries(ROUTE_SEGMENTS)) {
      const localeSegment = translations[loc];
      if (localeSegment !== deSegment) {
        translated = translated.replace(`/${deSegment}`, `/${localeSegment}`);
      }
    }
    entries.push({ locale: loc, path: `/${loc}${translated}` });
  }
  return entries;
}

/**
 * Build the full <link rel="alternate"> set for a page.
 *
 * LIVE-Guard: emits an entry for a non-CH storefront (e.g. de-AT →
 * bigfollow.at) ONLY when that storefront is in LIVE_STOREFRONTS. While
 * LIVE_STOREFRONTS === ['ch'] this function returns exactly the historical
 * CH set — same entries, same order, same attributes — so the CH regression
 * snapshot stays byte-identical.
 */
type AlternateLink = { hreflang: string; href: string };
export function buildAlternates(
  entries: HreflangEntry[],
  _siteUrl?: string,
): AlternateLink[] {
  // Each locale's alternate must point to its HOME domain, not the current
  // request domain. de / en / it / fr all live on the CH domain. Only the
  // per-storefront de-<COUNTRY> alternate points at its own domain (e.g.
  // de-AT → bigfollow.at). The canonical (rendered separately) is the only
  // tag that self-references the active request domain.
  const chUrl = `https://${getStorefrontConfig('ch').domain}`;
  const out: AlternateLink[] = entries.map((e) => ({
    hreflang: LOCALE_META[e.locale].hreflang,
    href: `${chUrl}${e.path}`,
  }));
  const dePath = entries.find((e) => e.locale === 'de')?.path;

  // Cross-storefront alternates for the German path. Gated by LIVE_STOREFRONTS.
  // Order: appended AFTER the locale entries and BEFORE x-default, so CH
  // (where the loop body never fires today) is unaffected. Each entry points
  // at its OWN home domain (bigfollow.at, bigfollow.de, …).
  if (dePath) {
    for (const sf of LIVE_STOREFRONTS) {
      if (sf === 'ch') continue; // CH is already covered by the de/de-CH entry
      const cfg = getStorefrontConfig(sf);
      if (cfg.defaultLocale !== 'de') continue; // only DE-speaking storefronts share the /de path
      const tag = `de-${cfg.countryCode}`; // e.g. de-AT
      out.push({ hreflang: tag, href: `https://${cfg.domain}${dePath}` });
    }
  }

  out.push({ hreflang: 'x-default', href: `${chUrl}${dePath || '/'}` });
  return out;
}

export default function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogType = 'website',
  ogImage,
  noindex = false,
  jsonLd,
  article,
  hreflangPaths,
  overrideScope,
}: SEOHeadProps) {
  const { locale } = useLanguage();
  const storefrontConfig = useStorefront();
  const siteUrl = LIVE_STOREFRONTS.includes(storefrontConfig.storefront)
    ? `https://${storefrontConfig.domain}`
    : SITE_URL;
  // Per-storefront overrides are keyed by (scope, scope_key). Default scope is
  // 'route' + pathname (SSR-safe). Callers with a stable identity (product,
  // category) pass `overrideScope` so the override survives slug changes and
  // covers all locales. CH-FASTPATH inside useSeoText returns `base`
  // immediately for storefront==='ch' — no query, no subscription.
  const routeKey = typeof window !== 'undefined' ? window.location.pathname : '/';
  const scope = overrideScope?.scope ?? 'route';
  const scopeKey = overrideScope?.scope_key ?? routeKey;
  const effectiveTitle = useSeoText(title, scope, scopeKey, 'title');
  const effectiveDescription = useSeoText(description, scope, scopeKey, 'description');
  // Suffix-Dedup: Wenn der (übersetzte) Titel bereits mit "| BigFollow"
  // endet (Legacy-Übersetzungen aus der DB), NICHT nochmals anhängen —
  // sonst entsteht "… | BigFollow | BigFollow".
  const suffix = ` | ${SITE_NAME}`;
  const alreadySuffixed =
    effectiveTitle === SITE_NAME || effectiveTitle.endsWith(suffix);
  const fullTitle = alreadySuffixed ? effectiveTitle : `${effectiveTitle}${suffix}`;
  const canonicalUrl = canonical
    ? rewriteCanonicalForStorefront(canonical, siteUrl)
    : (typeof window !== 'undefined' ? `${siteUrl}${window.location.pathname}` : siteUrl);
  const image = ogImage || `${siteUrl}/og-image.png`;
  const htmlLang = getHtmlLang(locale, storefrontConfig.countryCode);
  const ogLocale = htmlLang.replace('-', '_');
  const removeTwitterSite = storefrontConfig.storefront !== 'ch';

  useEffect(() => {
    document.title = fullTitle;
    document.documentElement.setAttribute('lang', htmlLang);

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'description', effectiveDescription);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', effectiveDescription);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:locale', ogLocale);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', effectiveDescription);
    setMeta('name', 'twitter:image', image);
    if (removeTwitterSite) {
      const existingTwitterSite = document.querySelector('meta[name="twitter:site"]') as HTMLMetaElement | null;
      existingTwitterSite?.remove();
    }

    if (ogType === 'article' && article) {
      if (article.publishedTime) setMeta('property', 'article:published_time', article.publishedTime);
      if (article.modifiedTime) setMeta('property', 'article:modified_time', article.modifiedTime);
      if (article.author) setMeta('property', 'article:author', article.author);
    }

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    // --- Hreflang tags ---
    // Remove old hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    if (hreflangPaths && hreflangPaths.length > 0) {
      for (const alt of buildAlternates(hreflangPaths, siteUrl)) {
        const hrefLink = document.createElement('link');
        hrefLink.setAttribute('rel', 'alternate');
        hrefLink.setAttribute('hreflang', alt.hreflang);
        hrefLink.setAttribute('href', alt.href);
        hrefLink.setAttribute('data-hreflang', 'true');
        document.head.appendChild(hrefLink);
      }
    }

    // --- JSON-LD ---
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach(s => s.remove());

    const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach(s => s.remove());
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    };
  }, [fullTitle, effectiveDescription, canonicalUrl, ogType, image, noindex, jsonLd, article, hreflangPaths, htmlLang, ogLocale, removeTwitterSite, siteUrl]);

  return null;
}

function rewriteCanonicalForStorefront(canonical: string, siteUrl: string): string {
  try {
    const parsed = new URL(canonical, siteUrl);
    return `${siteUrl}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return canonical.startsWith('/') ? `${siteUrl}${canonical}` : canonical;
  }
}

function getHtmlLang(locale: Locale, countryCode: string): string {
  if (locale === 'en') return 'en';
  return `${locale}-${countryCode}`;
}

// JSON-LD helpers
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description: DEFAULT_DESCRIPTION,
    address: { '@type': 'PostalAddress', addressCountry: 'CH' },
    sameAs: [
      'https://www.instagram.com/bigfollow.ch',
      'https://www.tiktok.com/@bigfollow.ch',
    ],
  };
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?s={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function productSchema(product: {
  name: string;
  description?: string;
  image?: string;
  slug: string;
  rating?: number;
  reviewCount?: number;
  priceRange?: { low: number; high: number };
  reviews?: { author: string; rating: number; comment: string; date: string; verifiedPurchase?: boolean }[];
}) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || '',
    url: `${SITE_URL}/produkt/${product.slug}`,
    brand: { '@type': 'Brand', name: SITE_NAME },
  };
  if (product.image) schema.image = product.image;
  if (product.rating && product.reviewCount) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.rating,
      reviewCount: product.reviewCount,
      bestRating: 5,
      worstRating: 1,
    };
  }
  if (product.priceRange) {
    schema.offers = {
      '@type': 'AggregateOffer',
      priceCurrency: 'CHF',
      lowPrice: product.priceRange.low,
      highPrice: product.priceRange.high,
      offerCount: 1,
      availability: 'https://schema.org/InStock',
    };
  }
  // Individual review schema
  if (product.reviews && product.reviews.length > 0) {
    schema.review = product.reviews.slice(0, 10).map(r => {
      const reviewObj: Record<string, unknown> = {
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author },
        datePublished: r.date,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
        reviewBody: r.comment,
      };
      if (r.verifiedPurchase) {
        reviewObj.publisher = { '@type': 'Organization', name: SITE_NAME };
        // Custom property for verified-buyer signal
        reviewObj.verifiedPurchase = true;
      }
      return reviewObj;
    });
  }
  return schema;
}

export function blogPostSchema(post: {
  title: string;
  description?: string;
  image?: string;
  slug: string;
  publishedAt?: string;
  modifiedAt?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description || '',
    url: `${SITE_URL}/${post.slug}`,
    image: post.image || DEFAULT_IMAGE,
    datePublished: post.publishedAt,
    dateModified: post.modifiedAt || post.publishedAt,
    author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/${post.slug}` },
  };
}

export function faqSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}
